import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import AuthShell from "./AuthShell";
import { track, identify } from "@/lib/analytics";
import { getPending, postAuthPath } from "@/lib/menuFunnel";
import { t } from "@/i18n";

const FIELDS = [
  ["name", t("register.votre_nom"), "text", t("register.camille_martin"), "name"],
  ["restaurant_name", t("common.nom_du_restaurant"), "text", t("register.le_bistrot_du_coin"), "organization"],
  ["email", "E-mail", "email", "vous@restaurant.fr", "email"],
  ["password", t("register.mot_de_passe_8_caracteres_min"), "password", "", "new-password"],
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", restaurant_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const started = useRef(false);
  const viewed = useRef(false);

  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    track("signup_page_viewed");
  }, []);

  const change = (key, value) => {
    if (!started.current) {
      started.current = true;
      track("signup_started", { first_field: key });
    }
    setForm({ ...form, [key]: value });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) return setError(t("register.le_mot_de_passe_doit_contenir_au_moins_8_c"));
    setLoading(true);
    try {
      const user = await register(form);
      identify(user);
      track("signup_completed");
      if (getPending()) track("signup_after_analysis");
      nav(postAuthPath());
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={t("register.creer_votre_espace")}
      subtitle={getPending() ? t("register.votre_analyse_est_prete_creez_votre_compte") : t("register.analysez_la_rentabilite_de_chaque_plat_en")}
      footer={<>{t("register.deja_inscrit")} <Link to="/login" className="font-semibold text-foreground underline-offset-4 hover:underline" data-testid="go-login-link">{t("common.se_connecter")}</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        {FIELDS.map(([key, label, type, ph, ac]) => (
          <div className="space-y-2" key={key}>
            <Label htmlFor={key}>{label}</Label>
            <Input id={key} type={type} required placeholder={ph} autoComplete={ac} data-testid={`signup-${key.replace("_", "-")}-input`} value={form[key]} onChange={(e) => change(key, e.target.value)} />
          </div>
        ))}
        {error && <p className="text-sm text-rose-500" data-testid="signup-error">{error}</p>}
        <Button type="submit" className="w-full active:scale-[0.98]" disabled={loading} data-testid="signup-submit-btn">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("common.creer_mon_compte")}
        </Button>
      </form>
    </AuthShell>
  );
}
